/**
 * Utility functions for filtering and searching Geek Bar Pulse flavors
 */

// Define the Flavor interface
export interface Flavor {
  id: string;
  name: string;
  productType: 'Pulse' | 'Pulse X';
  edition: string;
  categories: string[];
  description: string;
  nicotineStrength: number;
  puffCount: number;
  tasteProfile: string[];
  hasCooling: boolean;
  intensity: 'mild' | 'medium' | 'strong';
  releaseDate: string;
  averageRating: number;
  imagePath?: string;
}

// Define the Filters interface
export interface Filters {
  query?: string;
  productType?: 'Pulse' | 'Pulse X' | 'All';
  categories?: string[];
  intensity?: string;
  hasCooling?: boolean;
  isNewArrival?: boolean;
  newArrivalDays?: number;
  minRating?: number; // Minimum rating filter
}

/**
 * Search flavors by name, description, or categories
 * @param flavors - Array of flavor objects
 * @param query - Search query
 * @returns - Filtered array of flavors matching the query
 */
export const searchFlavors = (flavors: Flavor[], query: string): Flavor[] => {
  if (!query || query.trim() === '') {
    return flavors;
  }
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return flavors.filter(flavor => {
    // Search in name
    if (flavor.name.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in description
    if (flavor.description.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
    
    // Search in categories
    if (flavor.categories.some(category => 
      category.toLowerCase().includes(normalizedQuery)
    )) {
      return true;
    }
    
    // Search in taste profile
    if (flavor.tasteProfile.some(taste => 
      taste.toLowerCase().includes(normalizedQuery)
    )) {
      return true;
    }
    
    return false;
  });
};

/**
 * Filter flavors by categories
 * @param flavors - Array of flavor objects
 * @param categories - Array of category strings to filter by
 * @returns - Filtered array of flavors
 */
export const filterByCategories = (flavors: Flavor[], categories: string[]): Flavor[] => {
  if (!categories || categories.length === 0) {
    return flavors;
  }
  
  return flavors.filter(flavor => 
    flavor.categories.some(category => categories.includes(category))
  );
};

/**
 * Filter flavors by intensity
 * @param flavors - Array of flavor objects
 * @param intensity - Intensity to filter by (mild, medium, strong)
 * @returns - Filtered array of flavors
 */
export const filterByIntensity = (flavors: Flavor[], intensity: string): Flavor[] => {
  if (!intensity) {
    return flavors;
  }
  
  return flavors.filter(flavor => flavor.intensity === intensity);
};

/**
 * Filter flavors by cooling effect
 * @param flavors - Array of flavor objects
 * @param hasCooling - Whether to filter for cooling effect
 * @returns - Filtered array of flavors
 */
export const filterByCooling = (flavors: Flavor[], hasCooling: boolean): Flavor[] => {
  if (hasCooling === null || hasCooling === undefined) {
    return flavors;
  }
  
  return flavors.filter(flavor => flavor.hasCooling === hasCooling);
};

/**
 * Filter flavors by release date to show only new arrivals
 * @param flavors - Array of flavor objects
 * @param isNewArrival - Whether to filter for new arrivals
 * @param daysThreshold - Number of days to consider as "new" (default: 30)
 * @returns - Filtered array of flavors
 */
export const filterByNewArrivals = (
  flavors: Flavor[], 
  isNewArrival: boolean, 
  daysThreshold: number = 30
): Flavor[] => {
  if (!isNewArrival) {
    return flavors;
  }
  
  const now = new Date();
  const threshold = new Date(now.setDate(now.getDate() - daysThreshold));
  
  return flavors.filter(flavor => {
    const releaseDate = new Date(flavor.releaseDate);
    return releaseDate >= threshold;
  });
};

/**
 * Filter flavors by product type
 * @param flavors - Array of flavor objects
 * @param productType - Product type to filter by (Pulse, Pulse X, or All)
 * @returns - Filtered array of flavors
 */
export const filterByProductType = (flavors: Flavor[], productType?: 'Pulse' | 'Pulse X' | 'All'): Flavor[] => {
  if (!productType || productType === 'All') {
    return flavors;
  }
  
  return flavors.filter(flavor => flavor.productType === productType);
};

/**
 * Apply multiple filters to flavors
 * @param flavors - Array of flavor objects
 * @param filters - Object containing filter criteria
 * @returns - Filtered array of flavors
 */
export const applyFilters = (flavors: Flavor[], filters: Filters): Flavor[] => {
  let result = [...flavors];
  
  // Apply search query
  if (filters.query) {
    result = searchFlavors(result, filters.query);
  }
  
  // Apply product type filter
  if (filters.productType) {
    result = filterByProductType(result, filters.productType);
  }
  
  // Apply category filters
  if (filters.categories && filters.categories.length > 0) {
    result = filterByCategories(result, filters.categories);
  }
  
  // Apply intensity filter
  if (filters.intensity) {
    result = filterByIntensity(result, filters.intensity);
  }
  
  // Apply cooling filter
  if (filters.hasCooling !== null && filters.hasCooling !== undefined) {
    result = filterByCooling(result, filters.hasCooling);
  }
  
  // Apply new arrivals filter
  if (filters.isNewArrival) {
    result = filterByNewArrivals(result, filters.isNewArrival, filters.newArrivalDays);
  }
  
  // Apply minimum rating filter
  if (filters.minRating && filters.minRating > 0) {
    result = filterByMinRating(result, filters.minRating);
  }
  
  return result;
};

/**
 * Sort flavors by different criteria
 * @param flavors - Array of flavor objects
 * @param sortBy - Sort criterion (popularity, newest, rating)
 * @returns - Sorted array of flavors
 */
export const sortFlavors = (flavors: Flavor[], sortBy: string): Flavor[] => {
  if (!sortBy) {
    return flavors;
  }
  
  const sortedFlavors = [...flavors];
  
  switch (sortBy) {
    case 'popularity':
      // For now, we'll use rating as a proxy for popularity
      return sortedFlavors.sort((a, b) => b.averageRating - a.averageRating);
      
    case 'newest':
      return sortedFlavors.sort((a, b) => 
        new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );
      
    case 'rating':
      return sortedFlavors.sort((a, b) => b.averageRating - a.averageRating);
      
    default:
      return sortedFlavors;
  }
};

/**
 * Get all unique categories from flavor data
 * @param flavors - Array of flavor objects
 * @returns - Array of unique category strings
 */
export const getAllCategories = (flavors: Flavor[]): string[] => {
  const categoriesSet = new Set<string>();
  
  flavors.forEach(flavor => {
    flavor.categories.forEach(category => {
      categoriesSet.add(category);
    });
  });
  
  return Array.from(categoriesSet).sort();
};

/**
 * Get all unique taste profiles from flavor data
 * @param flavors - Array of flavor objects
 * @returns - Array of unique taste profile strings
 */
export const getAllTasteProfiles = (flavors: Flavor[]): string[] => {
  const tasteProfilesSet = new Set<string>();
  
  flavors.forEach(flavor => {
    flavor.tasteProfile.forEach(taste => {
      tasteProfilesSet.add(taste);
    });
  });
  
  return Array.from(tasteProfilesSet).sort();
};

/**
 * Get all unique product types from flavor data
 * @param flavors - Array of flavor objects
 * @returns - Array of unique product type strings
 */
export const getAllProductTypes = (flavors: Flavor[]): string[] => {
  const productTypesSet = new Set<string>();
  
  flavors.forEach(flavor => {
    productTypesSet.add(flavor.productType);
  });
  
  return Array.from(productTypesSet).sort();
};

/**
 * Filter flavors by minimum rating
 * @param flavors - Array of flavor objects
 * @param minRating - Minimum rating to filter by
 * @returns - Filtered array of flavors
 */
export const filterByMinRating = (flavors: Flavor[], minRating: number): Flavor[] => {
  if (!minRating || minRating <= 0) {
    return flavors;
  }
  
  return flavors.filter(flavor => flavor.averageRating >= minRating);
};

/**
 * Get all unique editions for a specific product type
 * @param flavors - Array of flavor objects
 * @param productType - Product type to filter by (Pulse, Pulse X, or All)
 * @returns - Array of unique edition strings
 */
export const getEditionsByProductType = (flavors: Flavor[], productType?: 'Pulse' | 'Pulse X' | 'All'): string[] => {
  const filteredFlavors = productType && productType !== 'All' 
    ? flavors.filter(flavor => flavor.productType === productType)
    : flavors;
  
  const editionsSet = new Set<string>();
  
  filteredFlavors.forEach(flavor => {
    editionsSet.add(flavor.edition);
  });
  
  return Array.from(editionsSet).sort();
};
